import { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { useAppearanceDelay, useStore } from 'hooks';
import {
  hideResizeObserverRunTimeError,
  lazyLoad,
  resolveSubdomain,
} from 'utils';
import { getCookie } from 'utils/cookies';
import { callsWS, commonWS, ws } from 'socketInstance';
import { AppLoader } from 'components';
import { message } from 'components/ui';

const AuthorizedApp = lazyLoad(
  () => import('./views/authorized'),
  (module) => module.AuthorizedApp
);
const UnauthorizedApp = lazyLoad(
  () => import('./views/unauthorized'),
  (module) => module.UnauthorizedApp
);

const App = () => {
  const { user, gis } = useStore();
  const { getUserInfo, getPermissions, statuses, isLoading } = user;
  const delayedLoading = useAppearanceDelay(isLoading);

  const isAuth = !!getCookie('csrftoken');
  const subdomain = resolveSubdomain();
  const drivenThrowLocation =
    window.location.pathname.includes('auth/drive_through');

  const initializeUser = useCallback(async () => {
    // Сокеты авторизуются по cookie (csrftoken уже есть — по нему и гейтится
    // initializeUser) и не зависят от ответов getUserInfo/getPermissions/
    // checkGISAccess. Поднимаем их сразу, параллельно инициализации, иначе
    // WS-соединение простаивало бы в ожидании самого медленного из этих
    // запросов. Просроченный токен сокет обработает сам (reconnect по
    // TOKEN_EXPIRED), поэтому ранний connect безопасен.
    ws.connect();
    commonWS.connect();
    callsWS.connect();

    try {
      await getUserInfo();
      await getPermissions();
      await gis.checkGISAccess();
    } catch (error) {
      console.error('Initialization error:', error);
    }
  }, [getUserInfo, getPermissions, gis]);

  useEffect(() => {
    const userInfoSettled =
      user.statuses.user_info.fulfilled || user.statuses.user_info.error;
    
    if (
      !delayedLoading &&
      userInfoSettled &&
      localStorage.getItem('__is_account_switched')
    ) {
      localStorage.removeItem('__is_account_switched');

      if (user.statuses.user_info.fulfilled) {
        message({
          type: 'success',
          text: `Вход в ${user.provider.str_name} выполнен`,
        });
      }
    }
  }, [
    delayedLoading,
    user.statuses.user_info.fulfilled,
    user.statuses.user_info.error,
    user.provider.str_name,
  ]);

  useEffect(() => {
    window.addEventListener('error', (e) => hideResizeObserverRunTimeError(e));

    return () =>
      window.removeEventListener('error', (e) =>
        hideResizeObserverRunTimeError(e)
      );
  }, []);

  useEffect(() => {
    if (
      statuses.user_info.readyToLoad &&
      statuses.permissions.readyToLoad &&
      isAuth &&
      !user.id &&
      !user.statuses.user_info.error &&
      !user.statuses.permissions.error
    ) {
      if (!drivenThrowLocation) {
        initializeUser();
      }
    }
  }, [
    statuses.user_info.readyToLoad,
    statuses.permissions.readyToLoad,
    isAuth,
    user.id,
    user.statuses.user_info.error,
    user.statuses.permissions.error,
    drivenThrowLocation,
    initializeUser,
  ]);

  useEffect(() => {
    const closeAllSockets = () => {
      ws.close();
    };

    // Закрываем сокеты только при реальной перезагрузке/закрытии страницы.
    // Не закрываем их на unmount App, чтобы в dev StrictMode не получать
    // manual close на этапе CONNECTING.
    window.addEventListener('beforeunload', closeAllSockets);

    return () => {
      window.removeEventListener('beforeunload', closeAllSockets);
    };
  }, []);

  useEffect(() => {
    if (isAuth) {
      return;
    }

    ws.close();
  }, [isAuth]);

  return (
    <HelmetProvider>
      <Helmet>
        <title>Личный кабинет | ЖКХ</title>
        <link
          rel="icon"
          type="image/x-icon"
          sizes="16x16"
          href={`/favicon-${subdomain || 'lk'}.eis24.me.ico`}
        />
      </Helmet>
      <BrowserRouter>
        {delayedLoading ? (
          <AppLoader />
        ) : (
          <>{isAuth ? <AuthorizedApp /> : <UnauthorizedApp />}</>
        )}
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default observer(App);
